import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal, ComponentType } from '@angular/cdk/portal';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Injector, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ConfirmValue, FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { EventManager } from '@ms/core/service/event-manager.service';
import { Content } from '@ngneat/overview';
import { LayoutService } from 'app/layout/layout.service';
import { Observable, Subscription, defer, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { CreateHotToastRef, DefaultDataType, HotToastClose, ToastOptions } from '@ngxpert/hot-toast/hot-toast.model';
import { HotToastService } from '@ngxpert/hot-toast/hot-toast.service';
import { uniqueId } from 'lodash-es';
import { LoadingOverlayComponent } from '../loading/loading-overlay.component';
import { ToasterMessage, ToasterMessageComponent } from '../toaster-message/toaster-message.component';
import { DialogCounterService } from './dialog-counter.service';

export interface IDialogConfig<T> {
    dialog: MatDialog;
    component: ComponentType<T>;
    config?: MatDialogConfig;
    closeEvent?: (val: any) => void;
}

export interface LoadingDialogConfig {
    panelClass?: string;
    hasBackdrop?: boolean;
    backdropClass?: string;
}

const LOADING_DEFAULT_CONFIG: LoadingDialogConfig = {
    hasBackdrop: true,
    backdropClass: 'dark-backdrop',
};

export interface ConfirmAction {
    caption: string;
    event: () => any;
}

export const LOADING_CONTAINER_DATA = new InjectionToken<any>('LOADING_CONTAINER_DATA');

@Injectable({
    providedIn: 'root',
})
export class DialogService {
    private openedDialogs: string[] = [];
    _toast: HotToastService;
    get toast(): HotToastService {
        if (!this._toast) {
            this._toast = this.injector.get(HotToastService);
        }
        return this._toast;
    }

    constructor(
        private dialog: MatDialog,
        private layoutService: LayoutService,
        private em: EventManager,
        private overlay: Overlay,
        @Inject(Injector) private injector: Injector
    ) {
        DialogCounterService.currentDialog = [];
    }

    public open<T>(
        component: ComponentType<T> | TemplateRef<any>,
        config?: MatDialogConfig,
        closeEvent?: (val: any) => any
    ): MatDialogRef<T, any> {
        if (!config) {
            config = {};
        }
        if (!config.width) {
            config.width = <string>config.minWidth;
        }
        const isMobile = this.layoutService.mobileModeForDialog();
        config.restoreFocus = true;
        //config.id = uniqueId();
        //config.enterAnimationDuration = '0ms';
        //config.exitAnimationDuration = '0ms';

        // config.maxHeight = '100%';
        if (config.autoFocus == undefined) {
            config.autoFocus = true;
        }
        if (config.hasBackdrop == undefined) {
            config.hasBackdrop = true;
        }

        if (!config.width) {
            config.minWidth = config.minWidth || '700px';
        }
        if (this.layoutService.mobileModeForDialog()) {
            config.width = `${window.innerHeight}px`;
            config.height = `${window.innerWidth}px`;
        }

        config.maxHeight = '100vh';
        config.maxWidth = '100vw';
        //config.minWidth = config.width;
        if (isMobile) {
            config.width = '100vw';
            config.height = '100%';
            config.minWidth = '100vw';
        } else {
            console.log('WEB');
            //config.minHeight = config.height;
            // for resizable dialog
            //config.width = 'min-content';
        }
        if (!config.data) {
            config.data = { DIALOG_ID: config.id };
        } else {
            config.data = { ...config.data, DIALOG_ID: config.id };
        }

        console.log('DIALOG_CONFIG', config);
        const dialogRef = this.dialog.open(component, config);

        console.log('Dialog-Config', config);
        const dialogSub = dialogRef.afterClosed().subscribe(
            val => {
                if (closeEvent) {
                    closeEvent(val);
                }
                DialogCounterService.currentDialog.pop();
            },
            () => {},
            () => {
                if (dialogSub) {
                    dialogSub.unsubscribe();
                }
            }
        );

        dialogRef.afterOpened().subscribe(() => {
            DialogCounterService.currentDialog.push(dialogRef.id);
        });
        return dialogRef;
    }

    public confirm(
        message: string,
        title?: string,
        yesEvent?: (any) => any,
        noEvent?: () => any,
        yesCaption?: string,
        noCaption?: string,
        options?: ConfirmValue[],
        completeEvent?: () => any,
        showCancel?: boolean
    ) {
        const config: MatDialogConfig = {
            maxWidth: '80vw',
            maxHeight: '80vh',
            autoFocus: true,
            disableClose: true,
            role: 'alertdialog',
            id: uniqueId(),
        };
        config.data = {
            confirmMessage: message,
            title,
            yesCaption: yesCaption || 'GENERAL.BUTTON.DELETE',
            noCaption: noCaption || 'GENERAL.BUTTON.CANCEL',
            options,
            showCancel,
        };
        config.autoFocus = true;

        const dialogRef = this.dialog.open(FuseConfirmDialogComponent, config);

        if (this.layoutService.mobile()) {
            // dialogRef._containerInstance._config.minWidth = 'none';
            dialogRef.updateSize('100%');
        }
        const smallDialogSubscription = this.layoutService.change.subscribe(mobile => {
            if (mobile) {
                dialogRef.updateSize('100%');
                // dialogRef._containerInstance._config.minWidth = 'none';
            }
        });
        const dialogSub = dialogRef.afterClosed().subscribe(
            result => {
                smallDialogSubscription.unsubscribe();
                if (options?.length > 0) {
                    if (yesEvent && result?.yesClick) {
                        yesEvent(result);
                    } else if (noEvent && !result) {
                        noEvent();
                    }
                } else {
                    if (yesEvent && result == 'yes') {
                        yesEvent(result);
                    } else if (noEvent && result == 'no') {
                        noEvent();
                    }
                }
                DialogCounterService.currentDialog.pop();
                // console.log('dialog_size', DialogService.currentDialog.length);
            },
            () => {},
            () => {
                if (completeEvent) {
                    completeEvent();
                }
                if (dialogSub) {
                    dialogSub.unsubscribe();
                }
            }
        );
        dialogRef.afterOpened().subscribe(() => {
            // console.log('dialog_id', dialogRef._containerInstance._id);
            DialogCounterService.currentDialog.push(dialogRef.id);
            // console.log('dialog_size', DialogService.currentDialog.length);
        });
    }

    public closeAll(): void {
        this.dialog.closeAll();
        this.toast.close();
        DialogCounterService.currentDialog = [];
    }

    public closeDialog(id: string) {
        const _dialog = this.dialog.getDialogById(id);
        if (_dialog) {
            _dialog.close();
        }
    }

    public closeLatestDialog() {
        if (DialogCounterService.currentDialog?.length > 0) {
            const id = DialogCounterService.currentDialog[DialogCounterService.currentDialog.length - 1];
            this.closeDialog(id);
        }
    }

    public closeToasters(): void {
        this.toast.close();
    }

    public closeSuccessToasters(): void {
        this.toast.closeSuccessToasters();
    }

    public close(id?: string): void {
        this.toast.close(id);
    }

    public success(
        message: Content,
        config?: ToastOptions<DefaultDataType>,
        duration?: number,
        detail?: string,
        completeEvent?: (result: HotToastClose) => any
    ): CreateHotToastRef<any> {
        if (!message) {
            return;
        }
        const configDefined = !!config;
        config = config ? config : { ...this.toast.defaultConfig };
        config.style = {
            'font-size': '16px',
            width: 'auto',
            'max-width': 'none',
            //'max-width': 'var(--hot-toast-max-width,' + (this.layoutService.mobile ? '100%' : '600px') + ')',
            'border-left': '8px solid #43a047',
            'background-color': 'white',
            'box-shadow': '0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22)',
        };
        if ((configDefined && config.position == undefined) || !configDefined) {
            config.position = this.layoutService.mobile() ? 'bottom-center' : 'top-right';
        }
        if (config.autoClose == undefined) {
            config.autoClose = true;
        }
        if (config.duration == undefined) {
            config.duration = duration ?? 200;
        }
        /*
        if (!config.id) {
            config.id = uniqueId();
        }
            */
        config.icon = '';
        //this.toast.success(message, config);
        config.data = {
            message,
            detail,
            type: 'success',
        };
        //this.toast.error(message, config);
        console.log('SUCCESS-TOAST-CONFIG', config);
        if (typeof message == 'string') {
            console.log('STRING');
            const toastRef = this.toast.show<ToasterMessage>(ToasterMessageComponent, config);
            if (completeEvent) {
                toastRef.afterClosed.subscribe(result => {
                    completeEvent(result);
                });
            }
            return toastRef;
        } else {
            const toastRef = this.toast.success(message, config);
            if (completeEvent) {
                toastRef.afterClosed.subscribe(result => {
                    completeEvent(result);
                });
            }
            return toastRef;
        }
    }

    public warning(message: Content, config?: ToastOptions<DefaultDataType>, detail?: string): CreateHotToastRef<any> {
        if (!message) {
            return;
        }
        //const configDeclared = !!config;
        const configDefined = !!config;
        config = config ? config : { ...this.toast.defaultConfig };
        config.style = {
            'font-size': '16px',
            width: 'auto',
            'max-width': 'none',
            //'max-width': 'var(--hot-toast-max-width,' + (this.layoutService.mobile ? '100%' : '600px') + ')',
            'border-left': '8px solid #ffa000',
            'background-color': 'white',
            'box-shadow':
                '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
        };
        if ((configDefined && config.position == undefined) || !configDefined) {
            config.position = this.layoutService.mobile() ? 'bottom-center' : 'top-right';
        }
        /*
        if (!config.id) {
            config.id = uniqueId();
        }
            */
        if (config.autoClose == undefined) {
            config.autoClose = true;
        }
        if (config.duration == undefined) {
            config.duration = 2000;
        }
        if (config.dismissible == undefined) {
            config.dismissible = true;
        }
        config.icon = '';
        this.em.broadcast({
            name: 'button-clicked',
            content: {},
        });
        //this.toast.warning(message, config);
        config.data = {
            message,
            detail,
            type: 'warning',
        };
        //this.toast.error(message, config);
        console.log('WARNING-TOAST-CONFIG', config);

        if (typeof message == 'string') {
            return this.toast.show<ToasterMessage>(ToasterMessageComponent, config);
        } else {
            return this.toast.warning(message, config);
        }
    }

    public info(message: Content, config?: ToastOptions<DefaultDataType>, detail?: string): CreateHotToastRef<any> {
        if (!message) {
            return;
        }
        const configDefined = !!config;
        config = config ? config : { ...this.toast.defaultConfig };
        if (!config.style) {
            config.style = {
                'font-size': '16px',
                width: 'auto',
                'max-width': 'none',
                //'max-width': 'var(--hot-toast-max-width,' + (this.layoutService.mobile ? '100%' : '600px') + ')',
                'border-left': '8px solid #1976d2',
                'background-color': 'white',
                'box-shadow':
                    '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
            };
        }
        if ((configDefined && config.position == undefined) || !configDefined) {
            config.position = this.layoutService.mobile() ? 'bottom-center' : 'top-right';
        }
        if (config.autoClose == undefined) {
            config.autoClose = true;
        }
        /*
        if (!config.id) {
            config.id = uniqueId();
        }
            */
        if (!config.duration == undefined) {
            config.duration = 2000;
        }

        if (config.dismissible == undefined) {
            config.dismissible = true;
        }
        config.icon = '';
        this.em.broadcast({
            name: 'button-clicked',
            content: {},
        });
        //this.toast.show(message, config);
        config.data = {
            message,
            detail,
            type: 'info',
        };
        //this.toast.error(message, config);
        console.log('INFO-TOAST-CONFIG', config);
        if (typeof message == 'string') {
            return this.toast.show<ToasterMessage>(ToasterMessageComponent, config);
        } else {
            return this.toast.info(message, config);
        }
    }

    public error(message: Content, detail?: string, config?: ToastOptions<DefaultDataType>): CreateHotToastRef<any> {
        if (!message) {
            return;
        }
        const configDefined = !!config;
        config = config ? config : { ...this.toast.defaultConfig };
        config.style = {
            'font-size': '16px',
            width: 'auto',
            //'max-width': 'var(--hot-toast-max-width,' + (this.layoutService.mobile ? '100%' : '600px') + ')',
            'max-width': 'none',
            'border-left': '8px solid #b92b27',
            'background-color': 'white',
            'box-shadow':
                '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
        };
        if ((configDefined && config.position == undefined) || !configDefined) {
            config.position = this.layoutService.mobile() ? 'bottom-center' : 'top-right';
        }
        if (config.autoClose == undefined) {
            config.autoClose = true;
        }
        if (config.duration == undefined) {
            config.duration = 10000;
        }
        /*
        if (!config.id) {
            config.id = uniqueId();
        }
            */
        if (config.dismissible == undefined) {
            config.dismissible = true;
        }
        config.icon = '';
        this.em.broadcast({
            name: 'button-clicked',
            content: {},
        });
        config.data = {
            message,
            detail,
            type: 'error',
        };
        //this.toast.error(message, config);
        console.log('ERROR-TOAST-CONFIG', config);
        if (typeof message == 'string') {
            return this.toast.show<ToasterMessage>(ToasterMessageComponent, config);
        } else {
            return this.toast.error(message, config);
        }
    }

    public mobileMode(width: string, height: string, dialogRef: MatDialogRef<any>): Subscription {
        if (this.layoutService.mobile()) {
            dialogRef.updateSize('100%', '100%');
        }
        return this.layoutService.change.subscribe(mobile => {
            if (mobile) {
                if (height) {
                    dialogRef.updateSize('100%', '100%');
                } else {
                    dialogRef.updateSize('100%');
                }
            } else {
                if (height) {
                    dialogRef.updateSize(width, height);
                } else {
                    const _width =
                        width != undefined && width.indexOf('px') == -1 && width != 'px' ? `${width}px` : '500px';
                    dialogRef.updateSize(_width);
                }
            }
        });
    }

    observe<T>(
        loadingMessage: string,
        successMessage?: string,
        errorMessage?: string
    ): (source: Observable<T>) => Observable<T> {
        return source => {
            let overlayRef: OverlayRef;
            let toastRef: CreateHotToastRef<unknown>;
            const config: ToastOptions<DefaultDataType> = { ...this.toast.defaultConfig };
            if (loadingMessage) {
                config.style = {
                    'padding-top': '20px',
                    'padding-bottom': '20px',
                    'padding-left': '16px',
                    'padding-right': '16px',
                    'border-left': '8px solid #1976d2',
                    width: 'auto',
                    'max-width': 'none',
                    'font-size': '16px',
                    'box-shadow':
                        '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
                };
                config.position = 'bottom-right';
                config.autoClose = false;
                //config.duration = 60000000;
                //config.id = getUniqueId();
                //config.dismissible = false;
            }

            let updateMessage = this.em.subscribe('loading-message-update', message => {
                toastRef.updateMessage(`${loadingMessage}<br>${message}`);
            });

            return defer(() => {
                overlayRef = this.showOverlay();
                toastRef = this.toast.loading<DefaultDataType>(loadingMessage, config);
                return source.pipe(
                    tap({
                        ...(loadingMessage && {
                            next: () => {
                                //debugger;
                                console.log('Loading toast closing, next');
                                if (toastRef) {
                                    toastRef.close();
                                    this.hideOverlay(overlayRef);
                                    toastRef = undefined;
                                }
                                if (successMessage) {
                                    this.success(successMessage);
                                }
                            },
                        }),
                        ...(errorMessage && {
                            error: () => {
                                //debugger;
                                console.log('Loading toast closing, error');
                                if (toastRef) {
                                    toastRef.close();
                                    this.hideOverlay(overlayRef);
                                    toastRef = undefined;
                                }
                                this.error(errorMessage);
                            },
                        }),
                        complete: () => {
                            //debugger;
                            if (updateMessage) {
                                updateMessage.unsubscribe();
                                updateMessage = undefined;
                            }
                            if (toastRef) {
                                toastRef.close();
                                this.hideOverlay(overlayRef);
                                toastRef = undefined;
                            }
                        },
                    }),
                    catchError((error: HttpErrorResponse): Observable<any> => {
                        if (updateMessage) {
                            updateMessage.unsubscribe();
                            updateMessage = undefined;
                        }
                        console.log('Loading toast closing, http client error');
                        if (toastRef) {
                            toastRef.close();
                            this.hideOverlay(overlayRef);
                            toastRef = undefined;
                        }
                        return throwError(error);
                    })
                );
            });
        };
    }

    showAndObserve<T>(
        content: Content,
        successMessage?: string,
        errorMessage?: string
    ): (source: Observable<T>) => Observable<T> {
        return source => {
            let overlayRef: OverlayRef;
            let toastRef: CreateHotToastRef<unknown>;
            const config: ToastOptions<DefaultDataType> = { ...this.toast.defaultConfig };
            if (content) {
                config.style = {
                    'padding-top': '20px',
                    'padding-bottom': '20px',
                    'padding-left': '16px',
                    'padding-right': '16px',
                    width: 'auto',
                    'max-width': `var(--hot-toast-max-width,${this.layoutService.mobile() ? '100%' : '600px'})`,
                    'font-size': '16px',
                    'box-shadow':
                        '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
                };
                config.position = 'bottom-right';
                config.autoClose = true;
                config.duration = 60000000;
                //config.id = getUniqueId();
                config.dismissible = false;
                overlayRef = this.showOverlay();
                toastRef = this.toast.show<DefaultDataType>(content, config);
            }

            return source.pipe(
                tap({
                    next: () => {
                        console.log('Loading component, next');
                    },
                    ...(errorMessage && {
                        error: () => {
                            console.log('Loading component closing, error');
                            if (toastRef) {
                                toastRef.close();
                            }
                            this.hideOverlay(overlayRef);
                            this.error(errorMessage);
                        },
                    }),
                    complete: () => {
                        console.log('Loading component closing, complete');
                        if (toastRef) {
                            toastRef.close();
                        }
                        this.hideOverlay(overlayRef);
                        if (successMessage) {
                            this.success(successMessage);
                        }
                    },
                }),
                catchError((error: HttpErrorResponse): Observable<any> => {
                    console.log('Loading component closing, observable error');
                    if (toastRef) {
                        toastRef.close();
                    }
                    this.hideOverlay(overlayRef);
                    return throwError(error);
                })
            );
        };
    }

    showComponent(content: Content) {
        const overlay = this.showOverlay();
        const config: ToastOptions<DefaultDataType> = { ...this.toast.defaultConfig };
        config.style = {
            'padding-top': '20px',
            'padding-bottom': '20px',
            'padding-left': '16px',
            'padding-right': '16px',
            width: 'auto',
            'max-width': `var(--hot-toast-max-width,${this.layoutService.mobile() ? '100%' : '600px'})`,
            'font-size': '16px',
            'box-shadow':
                '0 3px 5px -1px rgb(0 0 0 / 20%), 0 6px 10px 0 rgb(0 0 0 / 14%), 0 1px 18px 0 rgb(0 0 0 / 12%)',
        };
        if (!config.position) {
            config.position = 'bottom-right';
        }
        config.autoClose = true;
        config.duration = 60000000;
        //config.id = getUniqueId();
        config.dismissible = false;
        return { toast: this.toast.show(content, config), overlay };
    }

    private showOverlay(): OverlayRef {
        // Returns an OverlayRef (which is a PortalHost)
        const dialogConfig = LOADING_DEFAULT_CONFIG;

        const overlayRef = this.createOverlay(dialogConfig);
        // Create ComponentPortal that can be attached to a PortalHost
        const loadingOverlayPortal = new ComponentPortal(LoadingOverlayComponent);
        // run in async context for triggering "tick", thus avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            overlayRef.attach(loadingOverlayPortal); // Attach ComponentPortal to PortalHost
        });
        return overlayRef;
    }

    private createOverlay(config: LoadingDialogConfig) {
        const overlayConfig = this.getOverlayConfig(config);
        return this.overlay.create(overlayConfig);
    }

    private getOverlayConfig(config: LoadingDialogConfig): OverlayConfig {
        const positionStrategy = this.overlay.position().global().centerHorizontally().centerVertically();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass: config.panelClass,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy,
        });
        return overlayConfig;
    }

    private hideOverlay(overlayRef: OverlayRef) {
        if (overlayRef) {
            overlayRef.detach();
            overlayRef.dispose();
        }
        overlayRef = undefined;
    }

    addOpenDialog(name: string) {
        if (!this.isDialogOpened(name)) {
            this.openedDialogs.push(name);
        }
    }

    isDialogOpened(name: string): boolean {
        return this.openedDialogs.findIndex(d => d == name) > -1;
    }

    removeOpenedDialog(name: string) {
        const idx = this.openedDialogs.findIndex(e => e == name);
        this.openedDialogs.splice(idx, 1);
    }

    closePopup() {
        this.em.broadcast({ name: 'close-popup', content: {} });
    }
}
